"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RecursiveDescentParser = void 0;
const token_type_1 = require("../lexical-analysis/token-type");
const atom_1 = require("./nodes/atom");
const binary_operation_1 = require("./nodes/binary-operation");
const syntax_exception_1 = require("./syntax-exception");
const syntax_factory_1 = require("./syntax-factory");
const syntax_kind_1 = require("./syntax-kind");
class RecursiveDescentParser {
    constructor() {
        this.currentIndex = 0;
        this.tokens = [];
    }
    get currentToken() {
        return this.tokens[this.currentIndex];
    }
    static createExpectedException(expected, actual) {
        const message = actual === 'EOF' ? `Expected '${expected}' but reached end of input.` : `Expected '${expected}' but got '${actual}'`;
        return new syntax_exception_1.SyntaxException(message);
    }
    static createUnexpectedException(actual) {
        const message = actual === 'EOF' ? 'Unexpected end of input.' : `Unexpected token '${actual}'`;
        return new syntax_exception_1.SyntaxException(message);
    }
    parse(tokens) {
        this.currentIndex = 0;
        this.tokens = tokens;
        return this.parseExpression();
    }
    tryConsumeToken(type) {
        if (this.currentToken.type !== type) {
            return false;
        }
        this.currentIndex++;
        return true;
    }
    consumeToken(type, lexeme) {
        const token = this.currentToken;
        if (token.type !== type) {
            throw RecursiveDescentParser.createExpectedException(lexeme, this.currentToken.value);
        }
        this.currentIndex++;
        return token;
    }
    parseExpression() {
        return this.parseOrTerm();
    }
    parseOrTerm() {
        let node = this.parseAndTerm();
        while (this.tryConsumeToken(token_type_1.TokenType.Or)) {
            node = syntax_factory_1.SyntaxFactory.Or(node, this.parseAndTerm());
        }
        return node;
    }
    parseAndTerm() {
        let node = this.parseFactor();
        while (this.tryConsumeToken(token_type_1.TokenType.And)) {
            node = syntax_factory_1.SyntaxFactory.And(node, this.parseFactor());
        }
        return node;
    }
    parseFactor() {
        if (this.currentToken.type === token_type_1.TokenType.Not) {
            return this.parseNotFactor();
        }
        if (this.currentToken.type === token_type_1.TokenType.RoundBracketLeft) {
            return this.parseSubExpression();
        }
        return this.parseComparison();
    }
    parseNotFactor() {
        this.consumeToken(token_type_1.TokenType.Not, 'NOT');
        return syntax_factory_1.SyntaxFactory.Not(this.parseFactor());
    }
    parseSubExpression() {
        this.consumeToken(token_type_1.TokenType.RoundBracketLeft, '(');
        const node = this.parseExpression();
        this.consumeToken(token_type_1.TokenType.RoundBracketRight, ')');
        return node;
    }
    parseComparison() {
        const leftOperand = this.parseAtom();
        const operatorKind = this.parseComparisonKind();
        if (operatorKind === null) {
            throw RecursiveDescentParser.createUnexpectedException(this.currentToken.value);
        }
        this.currentIndex++;
        const rightOperand = this.parseAtom();
        return new binary_operation_1.BinaryOperation(operatorKind, leftOperand, rightOperand);
    }
    parseAtom() {
        if (this.currentToken.type === token_type_1.TokenType.Identifier) {
            return this.parseIdentifier();
        }
        if (this.currentToken.type === token_type_1.TokenType.SquareBracketLeft) {
            return this.parseCollection();
        }
        return this.parseLiteral();
    }
    parseIdentifier() {
        const token = this.consumeToken(token_type_1.TokenType.Identifier, 'identifier');
        return syntax_factory_1.SyntaxFactory.Identifier(token.value);
    }
    parseCollection() {
        this.consumeToken(token_type_1.TokenType.SquareBracketLeft, '[');
        const elements = this.parseCollectionElements();
        this.consumeToken(token_type_1.TokenType.SquareBracketRight, ']');
        return syntax_factory_1.SyntaxFactory.Collection(elements);
    }
    parseCollectionElements() {
        if (this.currentToken.type === token_type_1.TokenType.SquareBracketRight) {
            return [];
        }
        const firstElement = this.parseLiteral();
        if (firstElement === null) {
            throw RecursiveDescentParser.createUnexpectedException(this.currentToken.value);
        }
        return [firstElement, ...this.parseRemainingCollectionElements()];
    }
    parseRemainingCollectionElements() {
        const remainingElements = [];
        while (this.currentToken.type !== token_type_1.TokenType.SquareBracketRight) {
            remainingElements.push(this.parseSuccessiveCollectionElement());
        }
        return remainingElements;
    }
    parseSuccessiveCollectionElement() {
        this.consumeToken(token_type_1.TokenType.Comma, ',');
        const element = this.parseLiteral();
        if (element === null) {
            throw RecursiveDescentParser.createUnexpectedException(this.currentToken.value);
        }
        return element;
    }
    parseLiteral() {
        const literalKind = this.parseLiteralKind();
        if (literalKind === null) {
            throw RecursiveDescentParser.createUnexpectedException(this.currentToken.value);
        }
        const result = new atom_1.Atom(literalKind, this.currentToken.value);
        this.currentIndex++;
        return result;
    }
    parseLiteralKind() {
        switch (this.currentToken.type) {
            case token_type_1.TokenType.String:
                return syntax_kind_1.SyntaxKind.StringLiteral;
            case token_type_1.TokenType.RegExp:
                return syntax_kind_1.SyntaxKind.RegExpLiteral;
            case token_type_1.TokenType.Number:
                return syntax_kind_1.SyntaxKind.NumberLiteral;
            case token_type_1.TokenType.Boolean:
                return syntax_kind_1.SyntaxKind.BooleanLiteral;
            case token_type_1.TokenType.Date:
                return syntax_kind_1.SyntaxKind.DateLiteral;
            case token_type_1.TokenType.Null:
                return syntax_kind_1.SyntaxKind.NullLiteral;
            case token_type_1.TokenType.Uuid:
                return syntax_kind_1.SyntaxKind.UuidLiteral;
            default:
                return null;
        }
    }
    parseComparisonKind() {
        switch (this.currentToken.type) {
            case token_type_1.TokenType.Equal:
                return syntax_kind_1.SyntaxKind.EqualOperation;
            case token_type_1.TokenType.NotEqual:
                return syntax_kind_1.SyntaxKind.NotEqualOperation;
            case token_type_1.TokenType.LessThanOrEqual:
                return syntax_kind_1.SyntaxKind.LessThanOrEqualOperation;
            case token_type_1.TokenType.LessThan:
                return syntax_kind_1.SyntaxKind.LessThanOperation;
            case token_type_1.TokenType.GreaterThanOrEqual:
                return syntax_kind_1.SyntaxKind.GreaterThanOrEqualOperation;
            case token_type_1.TokenType.GreaterThan:
                return syntax_kind_1.SyntaxKind.GreaterThanOperation;
            case token_type_1.TokenType.Match:
                return syntax_kind_1.SyntaxKind.MatchOperation;
            case token_type_1.TokenType.NotMatch:
                return syntax_kind_1.SyntaxKind.NotMatchOperation;
            case token_type_1.TokenType.Subset:
                return syntax_kind_1.SyntaxKind.SubsetOperation;
            case token_type_1.TokenType.NotSubset:
                return syntax_kind_1.SyntaxKind.NotSubsetOperation;
            case token_type_1.TokenType.Superset:
                return syntax_kind_1.SyntaxKind.SupersetOperation;
            case token_type_1.TokenType.NotSuperset:
                return syntax_kind_1.SyntaxKind.NotSupersetOperation;
            default:
                return null;
        }
    }
}
exports.RecursiveDescentParser = RecursiveDescentParser;
//# sourceMappingURL=recursive-descent-parser.js.map